/**
 * Civil-twilight day/night decision (spec 07), computed offline from a
 * NOAA solar-position approximation — no network at sea. "Night" = the sun is
 * below the civil-twilight threshold (zenith 96° / altitude −6°).
 *
 * Accuracy is ~1 minute, far better than the day/night switch needs.
 */

const CIVIL_ALTITUDE_DEG = -6;

const rad = (d: number): number => (d * Math.PI) / 180;
const deg = (r: number): number => (r * 180) / Math.PI;

/** Julian day number for a UTC instant. */
function toJulian(ms: number): number {
  return ms / 86_400_000 + 2440587.5;
}

/** Solar altitude (degrees above horizon) at a position and instant. */
export function solarAltitudeDeg(lat: number, lon: number, atMs: number): number {
  const jd = toJulian(atMs);
  const n = jd - 2451545.0; // days since J2000.0

  const meanLong = (280.46 + 0.9856474 * n) % 360;
  const meanAnom = rad((357.528 + 0.9856003 * n) % 360);
  const eclipticLong = rad(
    meanLong + 1.915 * Math.sin(meanAnom) + 0.02 * Math.sin(2 * meanAnom),
  );

  const obliquity = rad(23.439 - 0.0000004 * n);
  const declination = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLong));

  // Right ascension, kept in the same quadrant as the ecliptic longitude.
  const rightAsc = Math.atan2(Math.cos(obliquity) * Math.sin(eclipticLong), Math.cos(eclipticLong));

  // Greenwich mean sidereal time → local hour angle.
  const gmst = (280.46061837 + 360.98564736629 * n) % 360;
  const lst = rad((gmst + lon) % 360);
  const hourAngle = lst - rightAsc;

  const latR = rad(lat);
  const altitude = Math.asin(
    Math.sin(latR) * Math.sin(declination) +
      Math.cos(latR) * Math.cos(declination) * Math.cos(hourAngle),
  );
  return deg(altitude);
}

/** True when it is civil-twilight-dark or darker at the position/instant. */
export function isNight(lat: number, lon: number, atMs: number): boolean {
  return solarAltitudeDeg(lat, lon, atMs) < CIVIL_ALTITUDE_DEG;
}
